import { Fragment } from 'react';
import { Box, Typography, Grid, FormHelperText } from "@mui/material"
import { GridItems } from '../../GridItems';
import TooltipText from "../../../inputComponents/TooltipText"
import { CheckboxComponent } from "../../../inputComponents/CheckboxComponent"
import { CurrencyDetailsSubtitles, MechanicType, TooltipsForMechanic } from "../../../../constants/currency-constants"
import DisableSelectedItemsDropdown from "../../../../common/DisableSelectedItemsDropdown/DisableSelectedItemsDropdown"
import { getIn, FormikProps } from 'formik';
import { CurrencyTableItem } from '../../../../types/componentTypes/CurrencyTypes';
import { removeCheckerLambdasWhenEmpty } from '../../../../pages/EditPromotion/LimitsTab/helpers/limit-helpers';

interface CurrencyEntryProps {
    formik: FormikProps<any>;
    mechanic: MechanicType;
    isEdit: boolean;
    providedCurrencies: CurrencyTableItem[];
    currencyList: string[];
    selectedCurrencies: string[];
    currencyAllocations: string[];
    setSelectedCurrencies: React.Dispatch<React.SetStateAction<string[]>>;
    setCurrencyAllocations: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}

const CurrencyEntry = ({
    formik,
    mechanic,
    isEdit,
    providedCurrencies,
    currencyList,
    currencyAllocations,
    setCurrencyAllocations,
    selectedCurrencies,
    setSelectedCurrencies
}: CurrencyEntryProps) => {
    const currencyDropDownLabelConstructor = (option: string, inputValue: string, inputOptions: any[], dropDownOpened: any) => {
        return `${dropDownOpened ? option : inputValue} ${(inputOptions?.find((fullObjOption: any) =>
            fullObjOption.name === (dropDownOpened ? option : inputValue))?.currency_id) ?
            '(' + (inputOptions?.find((fullObjOption: any) =>
                fullObjOption.name === (dropDownOpened ? option : inputValue))?.currency_id) + ')' :
            'Select a Currency'}`
    }

    const setFormikCurrencyFields = (value: string, index: number) => {
        const filterValue = value || currencyAllocations[index];
        const selectedCurrency = providedCurrencies.find((curr) => curr.name === filterValue);
        if (!selectedCurrency) return;

        const tempCurrList = [...formik.values.configurationParameters.currencies];
        const currencyId = selectedCurrency.currency_id;

        if (!tempCurrList.includes(currencyId)) {
            tempCurrList.push(currencyId);
        }

        const currencyNames = tempCurrList.map((currId: string) =>
            formik.values.tempParams.availableCurrencies.find((currObject: { currency_id: any; }) => currObject.currency_id === currId)?.name
        );

        formik.setFieldValue('tempParams.currencyNames', currencyNames);
        formik.setFieldValue('configurationParameters.currencies', tempCurrList);
        formik.setFieldValue(`params.reduceAmount[${index}].currencyId`, currencyId);
    };

    const addCurrencyParameter = (props: {
        event: React.SyntheticEvent<Element, Event>;
        selectedItem: string;
        currentItemIndex: number;
    }) => {
        const { selectedItem, currentItemIndex } = props;
        const shouldUpdate = currencyAllocations[currentItemIndex];

        const updatedCurrencyAllocations = {
            ...currencyAllocations,
            [currentItemIndex]: selectedItem,
        };
        setCurrencyAllocations(updatedCurrencyAllocations);

        if (shouldUpdate) {
            const updatedSelectedCurrencies = [...selectedCurrencies];
            const currIndex = selectedCurrencies.indexOf(
                currencyAllocations[currentItemIndex]
            );
            updatedSelectedCurrencies[currIndex] = selectedItem;
            setSelectedCurrencies(updatedSelectedCurrencies);
        } else {
            setSelectedCurrencies([...selectedCurrencies, selectedItem]);
        }

        setFormikCurrencyFields(selectedItem, currentItemIndex);
    };

    const addRow = () => {
        formik.setFieldValue('params.reduceAmount', [
            ...formik.values.params.reduceAmount,
            { currencyId: '', amount: null },
        ]);
    };

    const removeRow = (params: { index: number; currName: string }) => {
        const values = getIn(formik.values, 'configurationParameters.currencies');
        formik.setFieldValue(
            'configurationParameters.currencies',
            values.filter((_: any, i: number) => i !== params.index)
        );
        formik.setFieldValue(
            'params.reduceAmount',
            formik.values.params.reduceAmount.filter(
                (_: any, i: number) => i !== params.index
            )
        );
        formik.setFieldValue(
            'tempParams.currencyNames',
            formik.values.tempParams.currencyNames.filter(
                (_: any, i: number) => i !== params.index
            )
        );

        const updatedSelectedCurrencies = [...selectedCurrencies].filter(
            (curr) => curr !== params.currName
        );
        setSelectedCurrencies(updatedSelectedCurrencies);
        setCurrencyAllocations({ ...updatedSelectedCurrencies });
    };

    const handleUnchecked = (event: any) => {
        const checked = event.target.checked;
        if (!checked) {
            formik.setFieldValue('flowLambdas', (formik.values.flowLambdas || []).filter((v: string) => v !== 'currencyReducer'));
            formik.setFieldValue('checkerLambdas', (formik.values.checkerLambdas || []).filter((v: string) => v !== 'currencyCheckerLambda'));
            formik.setFieldValue('params.reduceAmount', [{ currencyId: '', amount: null }]);
            removeCheckerLambdasWhenEmpty(formik.values);
            setSelectedCurrencies([]);
            setCurrencyAllocations({});
        } else {
                if (!(formik.values.flowLambdas || []).includes('currencyReducer')) {
                    formik.setFieldValue('flowLambdas', [...(formik.values.flowLambdas || []), 'currencyReducer']);
                }
                if (!(formik.values.checkerLambdas || []).includes('currencyCheckerLambda')) {
                    formik.setFieldValue('checkerLambdas', [...(formik.values.checkerLambdas || []), 'currencyCheckerLambda']);
                }
        }
    };

    return (
        <>
            <Box className='functionality-items'>
                <Box sx={{ paddingBottom: '10px' }}>
                    <Typography sx={{ marginBottom: '15px' }} variant='body1'>{CurrencyDetailsSubtitles.secondary[mechanic]}</Typography>
                    <TooltipText
                        dataTestId='currency-tooltip-second'
                        text=''
                        textVariant='body1'
                        tooltip={TooltipsForMechanic[mechanic]}
                        styleName={
                            isEdit
                                ? `currencies-secondary-tooltip-edit-${mechanic.replace(/ /g, '')}`
                                : `currencies-secondary-tooltip-${mechanic.replace(/ /g, '')}`
                        }
                    />
                </Box>
                <CheckboxComponent
                    customBoxClass='currencies-checkbox'
                    fieldName={'tempParams.useCurrency'}
                    label='Requires currency for participation'
                    customAction={handleUnchecked}
                />
            </Box>
            {formik.values.tempParams.useCurrency && (
                <>
                    <Grid container className='gridMainContainer'>
                        {formik.values.params.reduceAmount.map(
                            (_: string, index: number) => (
                                <Fragment key={`prize-type-currency-selection-${index}`}>
                                    <GridItems
                                        maxFields={5}
                                        testId={`currency-details-currency-dropdown-menu-${index}`}
                                        formik={formik}
                                        key={`currency-details-grid-item-${index}`}
                                        index={index}
                                        component={
                                            <DisableSelectedItemsDropdown
                                                inputComponentProps={{
                                                    uniqueKey: `currency-details-currency-input-${index}`,
                                                    label: 'Cost',
                                                    value: getIn(formik?.values, `params.reduceAmount[${index}]['amount']`) || 0,
                                                    formikLabel: `params.reduceAmount[${index}]['amount']`,
                                                    errorPath: `params.reduceAmount[${index}]['amount']`,
                                                    type: 'number',
                                                    max: Number.MAX_SAFE_INTEGER,
                                                }}
                                                dropDownProps={{
                                                    fullObjectOptionsList: providedCurrencies,
                                                    currentItemIndex: index,
                                                    label: 'Currency Name and ID',
                                                    placeholder: 'Select A Currency',
                                                    customLabelConstructor: currencyDropDownLabelConstructor,
                                                    testId: `prize-type-currency-dropdown-option-${index}`,
                                                    uniqueKey: `currency-details-currency-dropdown-${index}`,
                                                    value: currencyAllocations[index] || '',
                                                    optionsList: currencyList,
                                                    selectedItems: selectedCurrencies,
                                                    textFieldStyles: { background: 'white' },
                                                    onChangeHandler: addCurrencyParameter,
                                                }}
                                                formik={formik}
                                            />
                                        }
                                        addAction={addRow}
                                        removeAction={removeRow}
                                        formikValueArray='params.reduceAmount'
                                        itemClass='currency-details'
                                        customParam={{ currName: currencyAllocations[index] }}
                                    />
                                </Fragment>
                            )
                        )}
                        <FormHelperText error={true}>
                            {getIn(formik.touched, 'params.reduceAmount') &&
                                getIn(formik.errors, 'params.reduceAmount')}
                        </FormHelperText>
                    </Grid>
                </>
            )}
        </>
    )
}

export { CurrencyEntry }
